'use client';

import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { LazySignInModal } from '@/components/modals/lazy-sign-in-modal';
import { LazySignUpModal, prefetchSignUpModal } from '@/components/modals/lazy-sign-up-modal';
import { notify } from '@/components/ui/notify';
import type { TrialEntryPoint } from '@/context/types';
import { useAuth } from '@/hooks/use-auth';

interface TrialSignUpContextValue {
  openTrialSignUp: (entryPoint: TrialEntryPoint) => void;
}

type TrialPanel = 'sign-up' | 'sign-in';

export const TrialSignUpContext = createContext<TrialSignUpContextValue | null>(null);

export function TrialSignUpProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { t } = useTranslation('common');
  const router = useRouter();
  const { events } = router;
  const [panel, setPanel] = useState<TrialPanel | null>(null);
  // Outlives the panels on purpose: the sign-in panel reports "closed" and "sign-up requested" in the same
  // commit, and the second report still needs the entry point.
  const [entryPoint, setEntryPoint] = useState<TrialEntryPoint | null>(null);

  const openTrialSignUp = useCallback(
    (nextEntryPoint: TrialEntryPoint) => {
      // `customTrial` comes from /me and exists as soon as the account has a user_trial row — the api
      // allows one per account, so /trial could only tell this visitor the same thing one page later.
      if (user?.customTrial) {
        notify.info(t('login.trial_already_logged_in.reason.already_has_trial'));
        return;
      }

      if (user) {
        router.push('/trial');
        return;
      }

      setEntryPoint(nextEntryPoint);
      setPanel('sign-up');
    },
    [user, router, t]
  );

  const closePanels = useCallback(() => setPanel(null), []);

  const showSignIn = useCallback(() => setPanel('sign-in'), []);

  const handleSignInStateChange = useCallback((isOpen: boolean) => {
    if (!isOpen) setPanel((current) => (current === 'sign-in' ? null : current));
  }, []);

  const handleSignInSignUpRequest = useCallback((isSignUpOpen: boolean) => {
    if (isSignUpOpen) setPanel('sign-up');
  }, []);

  // _app keeps this provider mounted across navigations, and a locale switch does not even remount the
  // page, so a panel left open would follow the visitor onto the next page.
  useEffect(() => {
    events.on('routeChangeStart', closePanels);

    return () => {
      events.off('routeChangeStart', closePanels);
    };
  }, [events, closePanels]);

  const value = useMemo(() => ({ openTrialSignUp }), [openTrialSignUp]);

  return (
    <TrialSignUpContext.Provider value={value}>
      {children}
      {panel === 'sign-up' && entryPoint && <LazySignUpModal entryPoint={entryPoint} closeModalSingUp={closePanels} showModalSingIn={showSignIn} />}
      {panel === 'sign-in' && <LazySignInModal classNameButton='hidden' forceOpenModal onModalStateChange={handleSignInStateChange} onModalSignUpStateChange={handleSignInSignUpRequest} />}
    </TrialSignUpContext.Provider>
  );
}

/** Throws outside the provider: a CTA wired to nothing would fail silently in production. */
export function useTrialSignUp(): TrialSignUpContextValue {
  const context = useContext(TrialSignUpContext);

  if (!context) {
    throw new Error('useTrialSignUp must be used inside TrialSignUpProvider (mounted in _app)');
  }

  return context;
}

/** Warms the sign-up chunk from a gesture that precedes the click (pointer enter, keyboard focus). */
export function prefetchTrialSignUp(): void {
  prefetchSignUpModal();
}
